import React, { useEffect, useRef, useState } from 'react';
import './AfkChallenge.scss';

const PANEL_WIDTH = 360;

interface ShowEventDetail {
  word: string;
  kickInMs: number;
}

// "Are you still there?" prompt for AfkSystem's cancellable kick (see
// skymp5-server/ts/systems/afkSystem.ts). No cancel button on purpose - the
// whole point is to make sure someone's actually at the keyboard, so the
// only way out is typing the word back, or moving in-game (server sends
// afkChallengeCancelled, which just hides this without any input needed).
const AfkChallenge: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [word, setWord] = useState('');
  const [answer, setAnswer] = useState('');
  const [wrong, setWrong] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const showHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as ShowEventDetail;
      setWord(detail.word);
      setAnswer('');
      setWrong(false);
      setSecondsLeft(Math.max(0, Math.round(detail.kickInMs / 1000)));
      setVisible(true);
    };
    const hideHandler = () => setVisible(false);
    const wrongHandler = () => {
      setWrong(true);
      setAnswer('');
    };

    window.addEventListener('skymp5-client:showAfkChallenge', showHandler);
    window.addEventListener('skymp5-client:hideAfkChallenge', hideHandler);
    window.addEventListener('skymp5-client:afkChallengeWrongWord', wrongHandler);
    return () => {
      window.removeEventListener('skymp5-client:showAfkChallenge', showHandler);
      window.removeEventListener('skymp5-client:hideAfkChallenge', hideHandler);
      window.removeEventListener('skymp5-client:afkChallengeWrongWord', wrongHandler);
    };
  }, []);

  useEffect(() => {
    if (visible) inputRef.current?.focus();
  }, [visible]);

  // Purely cosmetic countdown - the server is the actual authority on when
  // the kick happens, this just gives a sense of urgency.
  useEffect(() => {
    if (!visible || secondsLeft <= 0) return;
    const id = setTimeout(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(id);
  }, [visible, secondsLeft]);

  const send = (message: string) => {
    if ((window as any).skyrimPlatform?.sendMessage) {
      (window as any).skyrimPlatform.sendMessage(message);
    }
  };

  const submit = () => {
    if (!answer.trim()) return;
    send(JSON.stringify({ action: 'afkChallengeSubmit', word: answer.trim() }));
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') submit();
  };

  if (!visible) return null;

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;

  return (
    <div className="afk-challenge-overlay">
      <div className="afk-challenge-panel skyrim-panel" style={{ width: `${PANEL_WIDTH}px` }}>
        <h1 className="afk-challenge-title">Es-tu toujours là ?</h1>
        <p className="afk-challenge-hint">
          Recopie le mot ci-dessous pour rester connecté
        </p>
        <div className="afk-challenge-word">{word}</div>
        <input
          ref={inputRef}
          className={'afk-challenge-input' + (wrong ? ' afk-challenge-input--wrong' : '')}
          type="text"
          value={answer}
          onChange={(e) => { setAnswer(e.target.value); setWrong(false); }}
          onKeyDown={onKeyDown}
          placeholder="Recopier le mot..."
          autoComplete="off"
        />
        {wrong ? <p className="afk-challenge-error">Mot incorrect, réessaie.</p> : null}
        <div className="afk-challenge-actions">
          <button type="button" className="afk-challenge-btn" onClick={submit}>
            Valider
          </button>
        </div>
        <p className="afk-challenge-countdown">
          Déconnexion dans {minutes}:{seconds.toString().padStart(2, '0')}
        </p>
      </div>
    </div>
  );
};

export default AfkChallenge;
